import { BadRequestException, Injectable, NotFoundException, Optional } from '@nestjs/common';
import { MediaObjectSource } from './media-object-source';
import { createHash } from 'node:crypto';
import { mkdir, readFile, rename, stat, writeFile } from 'node:fs/promises';
import * as path from 'node:path';
import sharp from 'sharp';

/**
 * Widths the storefront may request.
 *
 * A closed allowlist, not a clamp on an arbitrary number. This endpoint is
 * unauthenticated and each miss costs a full sharp decode+encode, so accepting
 * free-form widths would let anyone walk `?w=1..4000` and turn the image route
 * into a CPU exhaustion primitive. Three widths also keep the cache small and
 * the hit rate high.
 */
export const ALLOWED_WIDTHS = [400, 800, 1600] as const;
export type AllowedWidth = (typeof ALLOWED_WIDTHS)[number];

const SOURCE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.webp']);

export interface Derivative {
  body: Buffer;
  contentType: string;
}

/**
 * Serves resized WebP derivatives of already-uploaded images, generating them
 * on first request and caching to disk.
 *
 * **Why on demand rather than at upload time.** The plan called for emitting
 * 400/800/1600 derivatives inside the upload pipeline. That only helps images
 * uploaded *after* the change: every image already in the catalogue would keep
 * being served full-size to a 412 px viewport unless someone wrote and ran a
 * backfill over all of them. Generating on demand covers the existing
 * catalogue and new uploads with one code path and no migration, at the cost of
 * one slow request per (image, width) pair — after which it is a static file
 * read.
 *
 * The cache is keyed by a hash of the source path plus the source's mtime and
 * size, so replacing an image at the same path invalidates its derivatives
 * automatically instead of serving the previous picture forever.
 */
@Injectable()
export class ImageDerivativeService {
  /**
   * Optional so this service keeps working — and keeps its specs passing —
   * without the media stack bound. In production `MediaObjectSourceService`
   * supplies bucket-backed objects and this falls back to disk only for
   * objects that have not been migrated yet.
   */
  constructor(@Optional() private readonly objectSource?: MediaObjectSource) {}

  private readonly uploadsRoot = path.resolve(
    process.env.UPLOADS_DIR ?? path.join(process.cwd(), 'uploads'),
  );

  /** Cache lives beside the uploads it derives from, in a dotted subdirectory
   *  so it is never mistaken for user content or served by the static handler. */
  private readonly cacheRoot = path.join(this.uploadsRoot, '.derivatives');

  isAllowedWidth(value: unknown): value is AllowedWidth {
    return ALLOWED_WIDTHS.includes(Number(value) as AllowedWidth);
  }

  /**
   * Resolves a caller-supplied relative path to a real file inside the uploads
   * root, or throws.
   *
   * This is the security boundary of the whole endpoint. `src` arrives from a
   * query string, so it is treated as hostile: resolved to an absolute path and
   * then required to sit under the uploads root, which rejects `../` traversal,
   * absolute paths and symlink-style escapes alike. Comparing with a trailing
   * separator matters — a bare `startsWith(uploadsRoot)` would also accept a
   * sibling directory named `uploads-secrets`.
   */
  private resolveSource(src: string): string {
    if (!src || src.includes('\0')) throw new BadRequestException('Invalid image path');

    const absolute = path.resolve(this.uploadsRoot, src);
    const root = this.uploadsRoot + path.sep;
    if (!absolute.startsWith(root)) throw new BadRequestException('Invalid image path');

    // The cache is an output directory, not an input one.
    if (absolute.startsWith(this.cacheRoot + path.sep)) {
      throw new BadRequestException('Invalid image path');
    }

    if (!SOURCE_EXTENSIONS.has(path.extname(absolute).toLowerCase())) {
      throw new BadRequestException('Unsupported image type');
    }

    return absolute;
  }

  async get(src: string, width: number): Promise<Derivative> {
    if (!this.isAllowedWidth(width)) {
      throw new BadRequestException(
        `Unsupported width. Allowed: ${ALLOWED_WIDTHS.join(', ')}`,
      );
    }

    // Objects that live in a bucket are fetched through the storage adapter
    // that owns them; only pre-migration objects still on disk take the `fs`
    // path below. Without this branch, retiring local disk would silently stop
    // every derivative from resolving and the storefront would quietly fall
    // back to full-size originals — slower pages, no error anywhere.
    if (this.objectSource) {
      const remote = await this.objectSource.load(src);
      if (remote) return this.buildFromBuffer(remote.body, remote.cacheKey, width);
    }

    const absolute = this.resolveSource(src);

    let source: Awaited<ReturnType<typeof stat>>;
    try {
      source = await stat(absolute);
    } catch {
      throw new NotFoundException('Image not found');
    }
    if (!source.isFile()) throw new NotFoundException('Image not found');

    const cachePath = this.cachePath(absolute, width, source.mtimeMs, source.size);

    try {
      return { body: await readFile(cachePath), contentType: 'image/webp' };
    } catch {
      // Cache miss — fall through and build it.
    }

    const body = await sharp(await readFile(absolute))
      // `withoutEnlargement` keeps a 300 px source from being upscaled to 1600,
      // which would cost bytes to produce a blurrier picture than the original.
      .resize({ width, withoutEnlargement: true })
      .webp({ quality: 78 })
      .toBuffer();

    await this.writeCacheAtomically(cachePath, body);
    return { body, contentType: 'image/webp' };
  }

  /**
   * Resize path shared by both sources.
   *
   * The disk cache stays even for bucket-backed objects: it is derived,
   * disposable data — losing it costs one re-encode, not a customer's image —
   * and it is what keeps a cache hit to a file read instead of an egress
   * request plus a sharp decode on every storefront render.
   */
  private async buildFromBuffer(source: Buffer, cacheKey: string, width: number): Promise<Derivative> {
    const digest = createHash('sha256').update(`${cacheKey}|${width}`).digest('hex');
    const cachePath = path.join(this.cacheRoot, `${digest}.webp`);

    try {
      return { body: await readFile(cachePath), contentType: 'image/webp' };
    } catch {
      // Cache miss — build it.
    }

    const body = await sharp(source)
      .resize({ width, withoutEnlargement: true })
      .webp({ quality: 78 })
      .toBuffer();

    await this.writeCacheAtomically(cachePath, body);
    return { body, contentType: 'image/webp' };
  }

  /**
   * Flat cache filenames derived from a hash rather than a mirrored directory
   * tree: the hash folds the source path, mtime and size into one key, so a
   * re-uploaded image cannot collide with its own stale derivative.
   */
  private cachePath(absolute: string, width: number, mtimeMs: number, size: number): string {
    const digest = createHash('sha256')
      .update(`${absolute}|${mtimeMs}|${size}|${width}`)
      .digest('hex');
    return path.join(this.cacheRoot, `${digest}.webp`);
  }

  /**
   * Write to a unique temp file, then rename.
   *
   * Two concurrent requests for the same uncached derivative both encode; a
   * plain write would let one read a half-written file. Rename within a
   * filesystem is atomic, so a reader sees either no file or a complete one.
   */
  private async writeCacheAtomically(cachePath: string, body: Buffer): Promise<void> {
    try {
      await mkdir(path.dirname(cachePath), { recursive: true });
      const temp = `${cachePath}.${process.pid}.${Date.now()}.tmp`;
      await writeFile(temp, body);
      await rename(temp, cachePath);
    } catch {
      // A cache that cannot be written is a performance problem, not a
      // correctness one — the caller already has the bytes.
    }
  }
}
